import {
  DEFAULT_TOUR_SETTINGS,
  EMPTY_REPORT_TRACKER,
  LAYOUT,
  LIMITS,
  isAcknowledged,
  trackReports,
  type ClientMessage,
  type DeviceEventKind,
  type NavigationEndReason,
  type NavigationRoute,
  type Participant,
  type PageVisibility,
  type ReportTracker,
  type PositionSample,
  type ServerMessage,
  type ServerMessageOf,
  type StatusMessage,
  type StatusMessageKind,
  type Tour,
  type TourSettings,
  type TrackChunk,
  type TrackPoint,
  type TrackSegment,
  type ArrivalEstimate,
  type VehicleState,
  type Waypoint,
  type WaypointArrival,
} from '@konvoi/shared';

import { api } from './api.js';
import { ArrivalTracker } from './arrival.js';
import {
  bufferPosition,
  bufferPositions,
  bufferStatus,
  bufferedCount,
  bufferedStatusCount,
  dropBufferedStatus,
  peekBufferedStatus,
  dropBuffered,
  dropForeign,
  peekBuffered,
} from './buffer.js';
import { hinweisSchliesstSelbst } from './labels.js';
import { TourConnection, type ConnectionStatus } from './connection.js';
import { Geraetemeldungen } from './geraetemeldungen.js';
import { PositionTracker } from './geolocation.js';
import { ScreenWakeLock, type WakeLockState } from './wakeLock.js';
import { rememberTour, type StoredSession } from './session.js';
import { nachrichtenBeimWiederverbinden } from './wiederverbinden.js';

/**
 * Der Zustand einer laufenden Fahrt.
 *
 * Hier laufen Verbindung, Positionsermittlung, Bildschirmsperre und Puffer
 * zusammen. Die Oberfläche liest nur ab — sie enthält keine Fachlogik, damit
 * später eine andere Hülle darüber passt, ohne dass etwas nachgebaut werden
 * muss.
 */

export interface Warning {
  participantId: string;
  convoyState: VehicleState['convoyState'];
  delaySeconds: number | null;
  at: number;
}

export interface DriveState {
  tour: Tour | null;
  settings: TourSettings;
  participants: readonly Participant[];
  vehicles: readonly VehicleState[];
  segments: readonly TrackSegment[];
  /** Die Führungsspur je Abschnitt — über eine Pause hinweg wird nie durchgezogen. */
  track: ReadonlyMap<string, readonly TrackPoint[]>;
  youId: string | null;
  connection: ConnectionStatus;
  wakeLock: WakeLockState;
  /** Woran die Bildschirmsperre zuletzt scheiterte. `null`, solange nichts scheiterte. */
  wakeLockError: string | null;
  pageVisibility: PageVisibility;
  /** Wie viele Meldungen liegen ungesendet im Puffer? */
  buffered: number;
  /**
   * Wie viele Statusmeldungen auf die nächste Verbindung warten.
   *
   * Getrennt von `buffered` gezählt, weil es etwas anderes bedeutet: Eine
   * wartende Position ist ein Loch in der Spur, eine wartende **Panne** ist
   * Hilfe, die noch niemand angefordert hat.
   */
  bufferedStatus: number;
  /**
   * Seit wann das Gerät selbst „offline" meldet — oder `null`.
   *
   * Nicht dasselbe wie `connection`: Das ist, was die App aus ihrem Herzschlag
   * schließt. Dies ist, was der Browser sagt. Gezeigt wird es nur im
   * Prüfmodus, befolgt wird es gar nicht (A-139).
   */
  geraetOfflineSeit: number | null;
  statusMessages: readonly StatusMessage[];
  /**
   * Eingegangene Meldungen anderer, die auf diesem Gerät noch nicht
   * weggetippt wurden.
   *
   * Getrennt von `statusMessages`: Diese Liste ist der Verlauf und wächst,
   * jene ist das, was dem Fahrer gerade vor Augen steht. Eine Meldung nur in
   * eine Liste zu schreiben, die man erst aufklappen muss, heißt, sie
   * niemandem zu zeigen — wer fährt, klappt nichts auf.
   *
   * Beim Verbinden gefüllt wird sie nicht: Was während einer Unterbrechung
   * aufgelaufen ist, kommt mit dem Zustand der Ausfahrt und steht im Verlauf,
   * springt einen aber nicht Minuten später noch an.
   */
  incomingStatus: readonly StatusMessage[];
  /** Offene Warnungen für die Führungsansicht — bis sie quittiert werden. */
  warnings: readonly Warning[];
  navigationRequestedAt: number | null;
  /**
   * Die laufende Navigation zur Kolonne — Stufe 2.
   *
   * Der Server schickt sie neu, sooft die Führung weitergefahren ist; wo der
   * Fahrer auf ihr steht, rechnet dieses Gerät selbst aus. Die Anzeige hängt
   * damit nicht an der Verbindung: Im Funkloch bleibt die zuletzt bekannte
   * Route stehen und führt weiter.
   */
  navigationRoute: NavigationRoute | null;
  /**
   * Die geplante Route der Ausfahrt (Stufe 3).
   *
   * `null` heißt **nichts geplant** und nicht „noch nicht geladen": Der Server
   * schickt `route_plan` beim Beitritt ausdrücklich auch dann, wenn keine
   * Route vorliegt. Ohne diese Unterscheidung wüsste die Oberfläche nie, ob
   * sie noch warten soll.
   */
  routePlan: ServerMessageOf<'route_plan'>['route'];
  /**
   * Die Treffpunkte der Ausfahrt (Stufe 3).
   *
   * Kommen mit `route_plan`, **auch ohne Route**: Sie hängen an der Ausfahrt,
   * nicht an der Strecke. Stützpunkte sind nicht dabei — der Server lässt sie
   * weg, weil niemand dort hält und der Fahrer sie nicht sieht.
   */
  waypoints: readonly Waypoint[];
  /**
   * Wer an welchem Treffpunkt angekommen ist.
   *
   * Kommt vom Server, weil eine Ankunft eine gemeinsame Tatsache ist und nicht
   * die Meinung eines Geräts: Wer neben dem Sammelpunkt parkt, ist für alle
   * angekommen, auch wenn sein eigenes GPS gerade streut.
   */
  arrivals: readonly WaypointArrival[];
  /**
   * Der nächste Treffpunkt und die Fahrzeit dorthin.
   *
   * Auf diesem Gerät gerechnet, aus der Route, den Treffpunkten, der
   * Führungsspur und der eigenen Position — alles davon liegt bereits hier.
   * Damit läuft die Angabe **im Funkloch weiter**, und genau dort will jemand
   * wissen, wie weit es noch bis zur Rast ist.
   */
  arrival: ArrivalEstimate | null;
  /** Warum die letzte Navigation endete — Grundlage des Hinweises danach. */
  navigationEnded: NavigationEndReason | null;
  hint: string | null;
  /**
   * Schließt sich der Hinweis von selbst?
   *
   * Nur für die, die bestätigen, was ohnehin gut ist. Alles, was eine Störung
   * meldet, bleibt stehen, bis jemand es gelesen hat — siehe
   * `LAYOUT.selbstschliessendMs`.
   */
  hintSelfClearing: boolean;
  /**
   * Für wen gerade eine Rollenänderung unterwegs ist — sonst `null`.
   *
   * Eine Rollenänderung geht über die Verbindung raus und gilt erst, wenn das
   * neue `tour_state` zurückkommt. Bis dahin darf die Fläche nicht aussehen,
   * als sei nichts geschehen: Wer tippt und nichts sieht, tippt noch einmal —
   * und der zweite Tipp trifft die andere Verzweigung und nimmt die eben
   * übernommene Rolle wieder weg. Am 30.08.2026 auf der Ausfahrt 242008
   * genau so geschehen; die Rolle bestand 1,02 Sekunden.
   */
  rollenwechselFuer: string | null;
  /**
   * Wann zuletzt eine **neue** Meldung eines Fahrzeugs eintraf — nach der
   * eigenen Uhr, je Teilnehmer.
   *
   * Nicht `recordedAt` des Fahrzeugs: Das ist die Uhr des fremden Geräts, und
   * geht die falsch, ist jede daraus gerechnete Altersangabe falsch. In den
   * Daten steht dafür ein Beleg — eine Position mit Zeitstempel aus dem Jahr
   * 1995.
   *
   * Und nicht der Zeitpunkt der letzten Sammelmeldung: Die kommt jede Sekunde
   * für **alle** Fahrzeuge, auch für die, die längst nichts mehr gemeldet
   * haben. Daran gemessen wäre nie etwas veraltet.
   *
   * Vermerkt wird deshalb der Augenblick, in dem sich `recordedAt` eines
   * Fahrzeugs **geändert** hat. Das ist unabhängig davon, ob die fremde Uhr
   * richtig geht — sie muss sich nur bewegen.
   */
  lastReportAt: ReadonlyMap<string, number>;
  /**
   * Das Fahrzeug gehört nicht mehr zur Ausfahrt — selbst ausgetreten oder vom
   * Führungsfahrzeug herausgenommen. Danach wird nicht mehr verbunden.
   */
  removed: boolean;
  /**
   * Abweichung der eigenen Uhr von der des Servers, in Millisekunden.
   * Ein Gerät mit falsch gehender Uhr zeigte sonst einen falschen Rückstand.
   */
  clockOffsetMs: number;
  /**
   * Die letzte eigene Messung — Grundlage der Anzeige von Tempo und
   * Fahrtrichtung.
   *
   * Aus dem eigenen Gerät, nicht über den Server: Sie ist die frischeste
   * Quelle, und der Tacho arbeitet damit auch weiter, wenn die Verbindung
   * abgerissen ist und von der Kolonne nichts mehr zu sehen ist.
   *
   * `speedMps` und `headingDeg` fehlen, wenn das Gerät sie nicht liefert —
   * am Rechner ist das die Regel. Dann wird nichts angezeigt und nichts
   * gerechnet; ein erfundener Wert wäre schlimmer als keiner.
   */
  ownSample: PositionSample | null;
  /** Wann diese Messung eintraf, nach der eigenen Uhr. Grundlage für „veraltet". */
  ownSampleAt: number | null;
}

const INITIAL: DriveState = {
  tour: null,
  settings: DEFAULT_TOUR_SETTINGS,
  participants: [],
  vehicles: [],
  segments: [],
  track: new Map(),
  youId: null,
  connection: 'closed',
  wakeLock: 'inactive',
  wakeLockError: null,
  pageVisibility: 'foreground',
  buffered: 0,
  bufferedStatus: 0,
  geraetOfflineSeit: null,
  statusMessages: [],
  incomingStatus: [],
  warnings: [],
  navigationRequestedAt: null,
  navigationRoute: null,
  routePlan: null,
  waypoints: [],
  arrivals: [],
  arrival: null,
  navigationEnded: null,
  hint: null,
  hintSelfClearing: false,
  rollenwechselFuer: null,
  lastReportAt: new Map(),
  removed: false,
  clockOffsetMs: 0,
  ownSample: null,
  ownSampleAt: null,
};

export class DriveSession {
  #state: DriveState = INITIAL;
  #listeners = new Set<() => void>();
  #connection: TourConnection | null = null;
  #tracker: PositionTracker | null = null;
  #wakeLock: ScreenWakeLock | null = null;
  #session: StoredSession | null = null;
  #maxTrackSeq: number | null = null;
  /**
   * Hält den bisherigen Verlauf auf der geplanten Route fest.
   *
   * Hier und nicht in einer Komponente: Der Anker löst die Mehrdeutigkeit bei
   * Rundkursen und muss von einer Messung zur nächsten überleben. React darf
   * eine Komponente jederzeit neu aufbauen; dieser Zustand darf das nicht.
   */
  #ankunft = new ArrivalTracker();
  #onVisibilityChange: (() => void) | null = null;
  /** Was das Gerät über seine Verbindung meldet und noch zum Server muss (A-139). */
  #geraet = new Geraetemeldungen();
  #netzHoerer: { readonly offline: () => void; readonly online: () => void } | null = null;

  /**
   * Meldungen, die auf diesem Gerät bereits weggetippt wurden.
   *
   * Eine quittierte Meldung schickt der Server erneut an alle — nur so bekommt
   * die Führung die Liste der Gelesenen. Ohne dieses Gedächtnis stellte sich
   * die eigene Quittung selbst wieder in den Weg: Die Meldung käme
   * augenblicklich zurück und spränge erneut auf, und zwar endlos.
   */
  readonly #weggetippt = new Set<string>();

  /**
   * Quittungen, die noch nicht hinausgingen — im Funkloch getippt.
   *
   * Sie gehen beim Wiederverbinden hinaus. Bis dahin steht bei der Führung
   * „noch nicht gelesen", und das stimmt auch.
   */
  readonly #offeneQuittungen = new Set<string>();

  /**
   * Wann jedes Fahrzeug zuletzt etwas Neues gemeldet hat.
   *
   * Der Merker gehört nicht in den angezeigten Zustand: Er enthält zum
   * Vergleich auch die fremden Messzeiten, und die will niemand sehen. Nach
   * außen geht daraus nur `lastReportAt`.
   */
  #meldungen: ReportTracker = EMPTY_REPORT_TRACKER;
  /** Läuft eine Frist für eine unterwegs befindliche Rollenänderung? */
  #rollenwechselUhr: ReturnType<typeof setTimeout> | null = null;

  // -------------------------------------------------------------------------
  // Anbindung an React
  // -------------------------------------------------------------------------

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  snapshot = (): DriveState => this.#state;

  private patch(änderung: Partial<DriveState>): void {
    this.#state = { ...this.#state, ...änderung };
    for (const listener of this.#listeners) {
      listener();
    }
  }

  // -------------------------------------------------------------------------
  // Lauf
  // -------------------------------------------------------------------------

  async start(session: StoredSession, settings: TourSettings): Promise<void> {
    this.stop();
    this.#session = session;
    this.patch({ settings, youId: session.participantId });

    this.#wakeLock = new ScreenWakeLock((state) => {
      this.patch({ wakeLock: state, wakeLockError: this.#wakeLock?.lastError ?? null });
    });
    await this.#wakeLock.request();

    this.#tracker = new PositionTracker(settings, {
      onSample: (sample) => {
        void this.publish(sample);
      },
      /*
       * Eine verworfene Messung schließt sich von selbst (A-74, geändert am
       * 03.09.2026).
       *
       * Sie entsteht an genau einer Stelle: Die Ortung meldet eine Genauigkeit
       * schlechter als `minPositionAccuracyM`, und die Messung geht gar nicht
       * erst hinaus. Das ist gut zu wissen, wenn man zufällig hinsieht — es
       * erklärt eine Lücke in der eigenen Spur —, aber es verlangt nichts. Der
       * nächste Takt bringt schon die nächste Messung; wer sie wegtippen muss,
       * bedient am Steuer eine Fläche für eine Sache, die sich selbst erledigt.
       *
       * In A-74 stand sie zunächst in der Liste der Hinweise, die stehen
       * bleiben. Der Unterschied zu den beiden anderen dort: Ein Kartenfehler
       * und ein Ortungsfehler bleiben, bis jemand etwas tut. Eine einzelne zu
       * ungenaue Messung ist mit der nächsten vorbei.
       */
      onRejected: (grund) => {
        this.patch({ hint: grund, hintSelfClearing: hinweisSchliesstSelbst('messung_verworfen') });
      },
      /*
       * Der Ortungsfehler dagegen bleibt: Wer ihn nicht liest, wartet auf eine
       * Ortung, die nicht kommt.
       */
      onError: (meldung) => {
        this.patch({ hint: meldung, hintSelfClearing: hinweisSchliesstSelbst('ortungsfehler') });
      },
    });

    this.#connection = new TourConnection({
      url: session.webSocketUrl,
      tourId: session.tourId,
      participantId: session.participantId,
      sessionToken: session.sessionToken,
      knownTrackSeq: () => this.#maxTrackSeq,
      onMessage: (message) => {
        this.handle(message);
      },
      onStatus: (status) => {
        this.patch({ connection: status });
      },
      onReady: () => {
        void this.flushBuffer();
        void this.flushStatus();
        this.flushAcknowledgements();
        this.sendeGeraetemeldungen();
        /*
         * Was der Server mit der Verbindung vergessen hat, wird neu behauptet —
         * die Regel steht in `wiederverbinden.ts` und ist dort ohne Browser
         * geprüft. Bis zum 14.09.2026 stand sie hier als drei Zeilen, und
         * genau deshalb fiel niemandem auf, dass die Sichtbarkeit fehlte
         * (A-111).
         */
        for (const nachricht of nachrichtenBeimWiederverbinden({
          pageVisibility: this.#state.pageVisibility,
          navigationLaeuft: this.#state.navigationRoute !== null,
        })) {
          this.#connection?.send(nachricht);
        }
      },
      onUndelivered: (nachrichten) => {
        this.#geraet.zurueck(
          nachrichten.flatMap((n) => (n.type === 'device_event' ? [{ kind: n.kind, at: n.at }] : [])),
        );
        void this.rebufferUndelivered(nachrichten);
      },
      onGaveUp: (grund) => {
        this.geraetMeldet(grund === 'heartbeat' ? 'heartbeat_gave_up' : 'join_unanswered');
      },
    });

    this.#connection.open();
    this.#tracker.start();

    this.#onVisibilityChange = () => {
      void this.handleVisibility();
    };
    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    /*
     * Das Netzsignal des Geräts — mitgeschrieben, nicht befolgt (A-139). Die
     * Verbindung hat ihren eigenen Hörer auf `online` und handelt danach; dieser
     * hier schreibt nur auf.
     */
    this.#netzHoerer = {
      offline: () => {
        this.geraetMeldet('network_offline');
      },
      online: () => {
        this.geraetMeldet('network_online');
      },
    };
    window.addEventListener('offline', this.#netzHoerer.offline);
    window.addEventListener('online', this.#netzHoerer.online);
    if (!navigator.onLine) {
      this.geraetMeldet('network_offline');
    }

    /*
     * Der Puffer überdauert das Schließen der App. Was von einer früheren
     * Ausfahrt übrig ist, gehört nicht in diese: Der Server nähme es nicht an,
     * angezeigt würde es trotzdem — als warteten Meldungen auf Zustellung.
     */
    await dropForeign(session.tourId);
    this.patch({
      buffered: await bufferedCount(session.tourId),
      bufferedStatus: await bufferedStatusCount(session.tourId),
    });
  }

  stop(): void {
    this.#tracker?.stop();
    this.#connection?.close();
    void this.#wakeLock?.release();

    if (this.#onVisibilityChange !== null) {
      document.removeEventListener('visibilitychange', this.#onVisibilityChange);
      this.#onVisibilityChange = null;
    }
    if (this.#netzHoerer !== null) {
      window.removeEventListener('offline', this.#netzHoerer.offline);
      window.removeEventListener('online', this.#netzHoerer.online);
      this.#netzHoerer = null;
    }
    this.#geraet = new Geraetemeldungen();

    this.#tracker = null;
    this.#connection = null;
    this.#wakeLock = null;
    this.#maxTrackSeq = null;
    this.#weggetippt.clear();
    this.#offeneQuittungen.clear();
    this.#meldungen = EMPTY_REPORT_TRACKER;
    this.#state = INITIAL;
  }

  // -------------------------------------------------------------------------
  // Vordergrund-Bindung
  // -------------------------------------------------------------------------

  /**
   * Die Seite wechselt zwischen Vordergrund und Hintergrund.
   *
   * Im Hintergrund endet die Positionsermittlung — daran lässt sich nichts
   * ändern. Was sich ändern lässt: dass alle anderen es sofort erfahren, statt
   * eine eingefrorene Position für eine aktuelle zu halten.
   */
  private async handleVisibility(): Promise<void> {
    const sichtbar = document.visibilityState === 'visible';
    const zustand: PageVisibility = sichtbar ? 'foreground' : 'background';

    this.patch({
      pageVisibility: zustand,
      hint: sichtbar ? 'Willkommen zurück. Die Standortübertragung läuft wieder.' : null,
      hintSelfClearing: sichtbar && hinweisSchliesstSelbst('willkommen'),
    });

    this.#connection?.send({ type: 'visibility', state: zustand });

    if (sichtbar) {
      await this.#wakeLock?.reacquire();
      this.#tracker?.start();
      /*
       * Auch hier die angesammelte Wartezeit vergessen: Wer die App wieder
       * hervorholt, hat oft gerade das Funkloch verlassen — und im Hintergrund
       * ist die Wartezeit womöglich auf ihr Höchstmass gewachsen.
       */
      this.#connection?.jetztVersuchen();
      void this.flushBuffer();
    }
  }

  // -------------------------------------------------------------------------
  // Positionen
  // -------------------------------------------------------------------------

  /**
   * Den nächsten Treffpunkt neu bestimmen.
   *
   * Zwei Anlässe, und beide werden gebraucht:
   *
   *   * **Eine neue eigene Messung** — etwa einmal je Sekunde. Ausdrücklich auch
   *     ohne Verbindung: Route, Treffpunkte und Führungsspur liegen bereits auf
   *     dem Gerät, ein Funkloch hält die Zahl nicht an.
   *   * **Eine neue Route oder neue Treffpunkte.** Ohne diesen zweiten Anlass
   *     blieb die Leiste leer, solange das Fahrzeug stand: Beim Beitritt meldet
   *     das Gerät seine Position, bevor `route_plan` da ist — und wer auf dem
   *     Parkplatz wartet, meldet danach keine neue. Am 15.09.2026 im Browser
   *     gesehen, bevor irgendjemand damit gefahren ist.
   *
   * Genau derselbe Fall trifft den, der **an** einem Treffpunkt steht, während
   * der Organisator die Tour ändert. Der soll die Änderung sehen, ohne erst
   * losfahren zu müssen.
   */
  private rechneAnkunft(): void {
    const sample = this.#state.ownSample;
    if (sample === null) {
      return;
    }

    const vorher = this.#state.arrival;
    const jetzt = this.#ankunft.update({
      route: this.#state.routePlan,
      waypoints: this.#state.waypoints,
      own: sample,
      track: this.#state.track,
      segments: this.#state.segments,
      /*
       * Wo dieses Gerät schon war. Abseits der Route gilt der nächstgelegene
       * Treffpunkt — und einer, an dem man bereits angekommen ist, wäre
       * dauernd der nächstgelegene, solange man dort steht.
       */
      bereits: new Set(
        this.#state.arrivals
          .filter((a) => a.participantId === this.#state.youId)
          .map((a) => a.waypointId),
      ),
      at: Date.now(),
    });

    /*
     * Nur weitergeben, wenn sich etwas geändert hat. Jede Messung ein neues
     * Objekt zu setzen ließe jede Komponente, die daran hängt, im Sekundentakt
     * neu zeichnen — auf einem Telefon in der Sonne ist das Strom.
     */
    if (
      vorher?.waypoint.id === jetzt?.waypoint.id &&
      Math.round((vorher?.remainingM ?? -1) / 50) === Math.round((jetzt?.remainingM ?? -1) / 50) &&
      Math.round((vorher?.travelS ?? -1) / 30) === Math.round((jetzt?.travelS ?? -1) / 30)
    ) {
      return;
    }
    this.patch({ arrival: jetzt });
  }

  private async publish(sample: PositionSample): Promise<void> {
    // Zuerst anzeigen, dann senden: Die Anzeige von Tempo und Richtung hängt
    // nicht an der Verbindung — sie soll auch im Funkloch weiterlaufen.
    this.patch({ ownSample: sample, ownSampleAt: Date.now() });
    this.rechneAnkunft();

    const sitzung = this.#session;
    if (sitzung === null) {
      return;
    }

    /*
     * Liegt noch etwas im Puffer, geht auch die frische Messung durch ihn.
     *
     * Sonst überholte sie die wartenden, und der Server verwürfe diese danach
     * als älter als der bereits erreichte Stand — der Puffer hielte sein
     * Versprechen nie ein. Der nächste Takt reicht alles der Reihe nach nach.
     */
    if (this.#state.buffered > 0) {
      await bufferPosition(sitzung.tourId, sample);
      this.patch({ buffered: await bufferedCount(sitzung.tourId) });
      await this.flushBuffer();
      return;
    }

    const hinaus =
      this.#connection?.sendTracked({ type: 'position', position: sample }) ?? false;

    if (!hinaus) {
      await bufferPosition(sitzung.tourId, sample);
      this.patch({ buffered: await bufferedCount(sitzung.tourId) });
    }
  }

  /**
   * Was in eine Leitung ging, die sich danach als tot herausstellte.
   *
   * Bis zum 17.09.2026 war das verloren: Beim Hineinfahren ins Funkloch hielt
   * sich die App bis zu einer Minute für verbunden und sendete live ins Leere
   * (A-137). Die Verbindung gibt jetzt heraus, was kein `pong` bestätigt hat,
   * und es geht den Weg jeder anderen Messung ohne Netz.
   *
   * Der Puffer ordnet nach dem Zeitpunkt der Messung. Die Rückkehrer reihen
   * sich damit von selbst **vor** den Messungen ein, die seit der Erkennung
   * gepuffert wurden — und was der Server doch schon hat, verwirft er beim
   * Nachsenden als älter als seinen Stand.
   */
  private async rebufferUndelivered(nachrichten: readonly ClientMessage[]): Promise<void> {
    const sitzung = this.#session;
    if (sitzung === null) {
      return;
    }

    const messungen = nachrichten.flatMap((nachricht) => {
      switch (nachricht.type) {
        case 'position':
          return [nachricht.position];
        case 'positions_batch':
          return nachricht.positions;
        default:
          return [];
      }
    });
    if (messungen.length === 0) {
      return;
    }

    await bufferPositions(sitzung.tourId, messungen);
    this.patch({ buffered: await bufferedCount(sitzung.tourId) });
  }

  /**
   * Das Gerät meldet etwas über seine eigene Verbindung (A-139).
   *
   * Aufgeschrieben und an den Server geschickt, sonst nichts — die Verbindung
   * verhält sich deswegen nicht anders.
   */
  private geraetMeldet(kind: DeviceEventKind): void {
    this.#geraet.erfasse(kind, Date.now());
    if (this.#state.geraetOfflineSeit !== this.#geraet.offlineSeit) {
      this.patch({ geraetOfflineSeit: this.#geraet.offlineSeit });
    }
    this.sendeGeraetemeldungen();
  }

  /**
   * Die Gerätemeldungen hinaus — über das Zustellbuch.
   *
   * „Offline" meldet der Browser gerade dann, wenn die Leitung stirbt, und die
   * App hält sich in diesem Augenblick oft noch für verbunden. Ohne Nachhalten
   * ginge ausgerechnet diese Meldung in die tote Leitung (A-137). So kommt sie
   * beim Aufgeben zurück und geht mit dem nächsten Verbinden hinaus.
   */
  private sendeGeraetemeldungen(): void {
    const alle = this.#geraet.nimmAlle();
    for (let i = 0; i < alle.length; i += 1) {
      const meldung = alle[i];
      if (meldung === undefined) {
        continue;
      }
      const hinaus =
        this.#connection?.sendTracked({ type: 'device_event', kind: meldung.kind, at: meldung.at }) ??
        false;
      if (!hinaus) {
        this.#geraet.zurueck(alle.slice(i));
        return;
      }
    }
  }

  /** Nachsenden, sobald die Verbindung wieder steht. */
  private async flushBuffer(): Promise<void> {
    const sitzung = this.#session;
    if (this.#connection === null || sitzung === null) {
      return;
    }

    for (;;) {
      const bündel = await peekBuffered(sitzung.tourId, LIMITS.positionsPerBatch);
      if (bündel.length === 0) {
        break;
      }

      const hinaus = this.#connection.sendTracked({ type: 'positions_batch', positions: bündel });
      if (!hinaus) {
        break;
      }

      // Geräumt wird sofort, bestätigt ist damit noch nichts: Bis ein `pong`
      // die Sendung deckt, hält die Verbindung sie vor und gibt sie zurück,
      // falls die Leitung gleich wieder abreißt (A-137). Dass sie dann doppelt
      // ankommen kann, verträgt der Server — er verdoppelt keine Zeilen.
      await dropBuffered(bündel);
    }

    this.patch({ buffered: await bufferedCount(sitzung.tourId) });
  }

  // -------------------------------------------------------------------------
  // Nachrichten vom Server
  // -------------------------------------------------------------------------

  private handle(message: ServerMessage): void {
    switch (message.type) {
      case 'tour_state': {
        const spur = new Map(this.#state.track);
        this.mergeTrack(spur, message.track);
        /*
         * Name und Rolle merken — damit beim nächsten Öffnen beides sofort
         * dasteht: der Name auf der Schaltfläche der Startseite, die Rolle für
         * die Bedienung, die von ihr abhängt.
         */
        const selbst = message.participants.find((p) => p.id === message.youId);
        if (selbst !== undefined) {
          rememberTour(message.tour.id, message.tour.name, selbst.role);
        }
        /*
         * Jedes `tour_state` beendet eine laufende Rollenänderung: Es ist die
         * Antwort, auf die die Fläche gewartet hat, und es trägt die neue
         * Rolle bereits in sich.
         */
        if (this.#rollenwechselUhr !== null) {
          clearTimeout(this.#rollenwechselUhr);
          this.#rollenwechselUhr = null;
        }

        this.patch({
          tour: message.tour,
          settings: message.settings,
          participants: message.participants,
          rollenwechselFuer: null,
          vehicles: message.vehicles,
          segments: message.segments,
          track: spur,
          youId: message.youId,
          statusMessages: message.statusMessages,
          /*
           * Was während einer Unterbrechung aufgelaufen ist.
           *
           * Nachgeholt werden nur Ansagen der Führung, die dieses Gerät noch
           * nicht bestätigt hat: „Wir halten an" verliert seine Bedeutung
           * nicht dadurch, dass man gerade im Funkloch war. Alles andere ist
           * Verlauf und springt niemanden Minuten später noch an.
           */
          incomingStatus: this.vordergrund([
            ...this.#state.incomingStatus,
            ...message.statusMessages.filter(
              (meldung) =>
                isAcknowledged(meldung.kind) && this.stehtAus(meldung, message.youId),
            ),
          ]),
        });
        this.#tracker?.updateSettings(message.settings);
        return;
      }

      case 'positions': {
        // Die Regel steht in shared und ist dort geprüft — hier wird sie nur
        // fortgeschrieben.
        this.#meldungen = trackReports(this.#meldungen, message.vehicles, Date.now());

        this.patch({
          vehicles: message.vehicles,
          lastReportAt: this.#meldungen.times,
          clockOffsetMs: message.at - Date.now(),
        });
        return;
      }

      case 'leader_track_update': {
        const spur = new Map(this.#state.track);
        this.mergeTrack(spur, message.track);
        const abschnitte =
          message.segments.length === 0
            ? this.#state.segments
            : [
                ...this.#state.segments.filter(
                  (vorhanden) => !message.segments.some((neu) => neu.id === vorhanden.id),
                ),
                ...message.segments,
              ];
        this.patch({ track: spur, segments: abschnitte });
        return;
      }

      case 'lost_contact':
        this.patch({
          warnings: [
            ...this.#state.warnings.filter((w) => w.participantId !== message.participantId),
            {
              participantId: message.participantId,
              convoyState: message.convoyState,
              delaySeconds: message.delaySeconds,
              at: message.at,
            },
          ],
        });
        return;

      case 'contact_restored':
        this.patch({
          warnings: this.#state.warnings.filter((w) => w.participantId !== message.participantId),
        });
        return;

      case 'status_message': {
        const verlauf = [
          message.message,
          ...this.#state.statusMessages.filter((m) => m.id !== message.message.id),
        ].slice(0, 50);

        if (!this.stehtAus(message.message, this.#state.youId)) {
          this.patch({ statusMessages: verlauf });
          return;
        }

        this.patch({
          statusMessages: verlauf,
          incomingStatus: this.vordergrund([message.message, ...this.#state.incomingStatus]),
        });
        return;
      }

      case 'role_changed':
        // Die vollständige Teilnehmerliste folgt unmittelbar als tour_state.
        return;

      case 'navigation_requested':
        this.patch({ navigationRequestedAt: message.at });
        return;

      case 'navigation_route':
        // Die neue Route ersetzt die alte vollständig. Der Hinweis der Führung
        // hat sich damit erledigt — der Fahrer ist ja bereits unterwegs.
        this.patch({
          navigationRoute: message.route,
          navigationEnded: null,
          navigationRequestedAt: null,
        });
        return;

      case 'navigation_ended':
        this.patch({ navigationRoute: null, navigationEnded: message.reason });
        return;

      case 'error':
        /*
         * `removed` ist der einzige Fehler, nach dem es nichts mehr zu
         * versuchen gibt: Das Fahrzeug gehört nicht mehr zur Ausfahrt. Ohne
         * dieses Anhalten liefe die Wiederverbindung bis zum Ende der Fahrt
         * weiter, immer mit demselben Ergebnis — und auf dem Gerät stünde
         * unterdessen eine Kolonne, die es nicht mehr betrifft.
         */
        if (message.code === 'removed') {
          this.stop();
          this.patch({
            hint: message.message,
            hintSelfClearing: hinweisSchliesstSelbst('fahrt_vorbei'),
            removed: true,
          });
          return;
        }
        /*
         * Die Ausfahrt ist beendet — von der Führung oder weil der Letzte
         * gegangen ist. Wie bei `removed` gibt es nichts mehr zu versuchen:
         * Ohne Anhalten liefe die Wiederverbindung weiter, und auf dem Gerät
         * bewegte sich eine Kolonne, die es nicht mehr gibt.
         */
        if (message.code === 'tour_finished') {
          this.stop();
          this.patch({
            hint: message.message,
            hintSelfClearing: hinweisSchliesstSelbst('fahrt_vorbei'),
            removed: true,
          });
          return;
        }
        /*
         * `unauthorized` ist der Code, bei dem sich ein erneuter Versuch lohnt
         * — so steht es im Protokoll, und genau das tut die Wiederverbindung
         * ohnehin. Der Hinweis schließt sich deshalb von selbst, statt einen
         * Tipp zu verlangen.
         *
         * Am 09.09.2026 kam am Rand eines Funklochs „Keine Anmeldung
         * erhalten." als Kasten mit „Verstanden" — bei 66 km/h. Der nächste
         * Versuch gelang, der Kasten blieb (A-106).
         */
        this.patch({
          hint: message.message,
          hintSelfClearing: hinweisSchliesstSelbst(
            message.code === 'unauthorized' ? 'verbindung' : 'serverfehler',
          ),
        });
        return;

      case 'waypoint_arrivals':
        /*
         * Als Ganzes und nicht als Zuwachs: Die Liste ist klein, und ein
         * Zuwachs bräuchte einen Stand, den beide Seiten führen.
         */
        this.patch({ arrivals: message.arrivals });
        return;

      case 'route_plan':
        /*
         * Kommt beim Beitritt und danach nur, wenn sich die Route wirklich
         * geändert hat — nicht bei jedem `tour_state`. Eine Strecke mit
         * zwanzigtausend Punkten gehört nicht auf jede Leitung, die ohnehin
         * am Rand eines Funklochs arbeitet.
         */
        this.patch({ routePlan: message.route, waypoints: message.waypoints });
        // Sofort neu rechnen: Wer steht, meldet keine neue Position — und ohne
        // die bliebe die Leiste leer, bis er losfährt.
        this.rechneAnkunft();
        return;

      case 'pong':
        return;

      default: {
        const unbekannt: never = message;
        void unbekannt;
        return;
      }
    }
  }

  /**
   * Steht diese Meldung dem Fahrer noch bevor?
   *
   * Drei Gründe sprechen dagegen, und alle drei gelten unabhängig davon, ob
   * die Meldung gerade eintrifft oder mit dem Zustand der Ausfahrt nachkommt —
   * deshalb steht die Regel hier und nicht zweimal an den beiden Stellen.
   */
  private stehtAus(meldung: StatusMessage, youId: string | null): boolean {
    // Die eigene Meldung springt einen nicht selbst an; man hat sie gerade
    // abgeschickt.
    if (youId === null || meldung.participantId === youId) {
      return false;
    }
    // Auf diesem Gerät bereits weggetippt.
    if (this.#weggetippt.has(meldung.id)) {
      return false;
    }
    // Oder von diesem Gerät aus quittiert, bevor die App neu geladen wurde.
    return !meldung.acknowledgedBy.includes(youId);
  }

  /**
   * Was dem Fahrer vor Augen steht: das Neueste zuerst, jede Meldung einmal,
   * und nicht mehr, als die Karte verträgt.
   */
  private vordergrund(meldungen: readonly StatusMessage[]): readonly StatusMessage[] {
    const einmalig = new Map<string, StatusMessage>();
    for (const meldung of meldungen) {
      einmalig.set(meldung.id, meldung);
    }
    return [...einmalig.values()]
      .sort((a, b) => b.createdAt - a.createdAt)
      .slice(0, LIMITS.visibleStatusMessages);
  }

  /** Im Funkloch getippte Quittungen gehen beim Wiederverbinden hinaus. */
  private flushAcknowledgements(): void {
    for (const id of [...this.#offeneQuittungen]) {
      if (this.#connection?.send({ type: 'acknowledge_status', messageId: id }) === true) {
        this.#offeneQuittungen.delete(id);
      }
    }
  }

  /**
   * Nimmt Zuwachs der Führungsspur auf.
   *
   * Angehängt wird nur, was neu ist. Käme ein Punkt ein zweites Mal — etwa
   * weil nach einem Wiederverbinden mehr geschickt wurde als nötig —, liefe die
   * Linie auf der Karte zurück zum Anfang und wieder vor: ein Strich quer durch
   * die Landschaft, den nie jemand gefahren ist.
   */
  private mergeTrack(ziel: Map<string, readonly TrackPoint[]>, chunks: readonly TrackChunk[]): void {
    for (const chunk of chunks) {
      const vorhanden = ziel.get(chunk.segmentId) ?? [];
      const höchsteBekannte = vorhanden[vorhanden.length - 1]?.seq ?? -1;

      const neue = chunk.points
        .filter((punkt) => punkt.seq > höchsteBekannte)
        .sort((a, b) => a.seq - b.seq);

      if (neue.length > 0) {
        ziel.set(chunk.segmentId, [...vorhanden, ...neue]);
      }

      for (const punkt of chunk.points) {
        if (this.#maxTrackSeq === null || punkt.seq > this.#maxTrackSeq) {
          this.#maxTrackSeq = punkt.seq;
        }
      }
    }
  }

  // -------------------------------------------------------------------------
  // Handlungen des Fahrers
  // -------------------------------------------------------------------------

  /**
   * Eine Statusmeldung absetzen — und sie aufheben, wenn sie nicht hinausgeht.
   *
   * Das ist der einzige Fall, in dem wirklich **nachgereicht** wird. Eine
   * Panne entsteht einmal, auf einen Fingertipp hin; sie lässt sich nicht
   * ersetzen wie eine Position, und wenn sie verschwindet, merkt es niemand.
   * Bis zum 14.09.2026 geschah genau das: `send` gab `false` zurück, und hier
   * las niemand die Antwort.
   *
   * Der Zeitpunkt reist mit. Zugestellt wird sie später mit der Uhrzeit des
   * Tippens, nicht des Eingangs — sonst stünde eine Panne von vor zwanzig
   * Minuten als „gerade eben" in der Liste.
   */
  sendStatus(kind: StatusMessageKind): void {
    const eigene = this.#state.vehicles.find((f) => f.participantId === this.#state.youId);
    const ort = eigene === undefined ? {} : { lat: eigene.lat, lon: eigene.lon };
    const recordedAt = Date.now();

    if (this.#connection?.send({ type: 'status_message', kind, recordedAt, ...ort }) === true) {
      return;
    }

    const sitzung = this.#session;
    if (sitzung === null) {
      return;
    }
    void (async () => {
      await bufferStatus({
        recordedAt,
        tourId: sitzung.tourId,
        kind,
        lat: eigene?.lat ?? null,
        lon: eigene?.lon ?? null,
      });
      this.patch({
        bufferedStatus: await bufferedStatusCount(sitzung.tourId),
        hint: 'Die Meldung geht hinaus, sobald wieder Verbindung besteht.',
        hintSelfClearing: hinweisSchliesstSelbst('meldung_wartet'),
      });
    })();
  }

  /**
   * Wartende Statusmeldungen zustellen, sobald die Verbindung wieder steht.
   *
   * Eine nach der anderen und erst nach dem Erfolg aus dem Puffer genommen:
   * Reißt die Verbindung mitten im Nachsenden wieder ab, bleibt der Rest
   * stehen, statt verloren zu gehen.
   */
  private async flushStatus(): Promise<void> {
    const sitzung = this.#session;
    if (sitzung === null) {
      return;
    }

    for (const meldung of await peekBufferedStatus(sitzung.tourId)) {
      const ging = this.#connection?.send({
        type: 'status_message',
        kind: meldung.kind,
        recordedAt: meldung.recordedAt,
        ...(meldung.lat === null || meldung.lon === null
          ? {}
          : { lat: meldung.lat, lon: meldung.lon }),
      });
      if (ging !== true) {
        break;
      }
      await dropBufferedStatus([meldung]);
    }

    this.patch({ bufferedStatus: await bufferedStatusCount(sitzung.tourId) });
  }

  requestNavigationFor(participantId: string): void {
    if (this.#connection?.send({ type: 'request_navigation', participantId }) !== true) {
      this.nichtGesendet('Die Aufforderung ging nicht hinaus — es besteht gerade keine Verbindung.');
    }
  }

  /**
   * Die Navigation zur Kolonne starten — immer auf einen Fingertipp hin.
   *
   * Zwei Wege führen hierher: Der Fahrer hat den Hinweis der Führung angenommen
   * oder selbst gemerkt, dass er falsch ist. Von selbst startet nichts.
   */
  startNavigation(): void {
    if (this.#connection?.send({ type: 'navigation_start' }) !== true) {
      this.nichtGesendet(
        'Die Navigation lässt sich gerade nicht starten — es besteht keine Verbindung.',
      );
      return;
    }
    this.patch({ navigationRequestedAt: null, navigationEnded: null });
  }

  /**
   * Ein fehlgeschlagenes Beenden bleibt ohne Hinweis — und das ist richtig.
   *
   * Der Server vergisst eine Navigation ohnehin mit der Verbindung. Wer sie
   * ohne Netz beendet, bekommt genau das, was er wollte; eine Meldung wäre hier
   * eine Warnung vor nichts.
   */
  stopNavigation(): void {
    this.#connection?.send({ type: 'navigation_stop' });
    this.patch({ navigationRoute: null, navigationEnded: null });
  }

  /**
   * Ein Fahrzeug aus der Kolonne nehmen. Nur die Führung darf das; durchgesetzt
   * wird es am Server.
   */
  removeParticipant(participantId: string): void {
    if (this.#connection?.send({ type: 'remove_participant', participantId }) !== true) {
      this.nichtGesendet(
        'Das Fahrzeug ließ sich nicht aus der Kolonne nehmen — es besteht gerade keine Verbindung.',
      );
    }
  }

  /**
   * Ein Fingertipp, der nicht hinausging — dem Fahrer gesagt, statt verschluckt.
   *
   * Bis zum 14.09.2026 gab `send` hier eine Antwort, die niemand las. Wer bei
   * 70 km/h auf eine Fläche tippt und nichts geschieht, tippt noch einmal.
   */
  private nichtGesendet(satz: string): void {
    this.patch({ hint: satz, hintSelfClearing: hinweisSchliesstSelbst('nicht_gesendet') });
  }

  /**
   * Eine Rolle ändern — und dabei sagen, was geschieht.
   *
   * Vorher stand hier eine Zeile: senden und hoffen. Zwei Dinge fehlten, und
   * beide sind am 30.08.2026 auf der Ausfahrt 242008 aufgefallen.
   *
   * **Ohne Verbindung ging gar nichts raus** — das `?.` verschluckte den
   * Aufruf lautlos. Der Fahrer tippte ins Leere und erfuhr es nicht.
   *
   * **Und mit Verbindung sah man nichts.** Die Fläche beschriftet sich erst um,
   * wenn das neue `tour_state` zurückkommt. Wer in der Zwischenzeit ein zweites
   * Mal tippt, trifft die andere Verzweigung und nimmt die eben übernommene
   * Rolle wieder weg. Genau so bestand ein Schlusswagen 1,02 Sekunden lang.
   */
  changeRole(participantId: string, role: 'leader' | 'sweeper' | 'participant'): void {
    const ging = this.#connection?.send({ type: 'role_change', participantId, role }) ?? false;

    if (!ging) {
      this.patch({
        hint: 'Die Rolle ließ sich nicht ändern — es besteht gerade keine Verbindung.',
        hintSelfClearing: hinweisSchliesstSelbst('rollenwechsel'),
      });
      return;
    }

    this.patch({ rollenwechselFuer: participantId });

    /*
     * Bleibt die Antwort aus, gibt die Fläche auf. Ohne diese Frist stünde
     * „Einen Augenblick …" bis zum Ende der Fahrt, und das ist die schlechteste
     * aller Auskünfte: Sie sagt, es geschehe etwas, und es geschieht nichts.
     */
    if (this.#rollenwechselUhr !== null) {
      clearTimeout(this.#rollenwechselUhr);
    }
    this.#rollenwechselUhr = setTimeout(() => {
      this.#rollenwechselUhr = null;
      if (this.#state.rollenwechselFuer === null) {
        return;
      }
      this.patch({
        rollenwechselFuer: null,
        hint: 'Die Rolle ließ sich nicht ändern — der Server antwortet nicht.',
        hintSelfClearing: hinweisSchliesstSelbst('rollenwechsel'),
      });
    }, LAYOUT.rollenwechselFristMs);
  }

  /**
   * Sich für die anderen unsichtbar schalten — oder wieder zeigen.
   *
   * Danach wird die eigene Position nicht mehr weitergegeben. Der Teilnehmer
   * bleibt in der Liste; verschwinden würde niemandem helfen, und die Führung
   * soll wissen, dass jemand dabei ist, auch wenn sie ihn nicht ortet.
   *
   * Die Änderung geht über die HTTP-Schnittstelle, nicht über das Protokoll:
   * Sie ist selten und dauerhaft, und sie ist etwas anderes als die
   * Sichtbarkeit der Seite, die im Sekundentakt wechseln kann.
   */
  async setVisible(sichtbar: boolean): Promise<void> {
    const sitzung = this.#session;
    if (sitzung === null) {
      return;
    }

    try {
      await api.updateProfile(sitzung.tourId, sitzung.sessionToken, { visible: sichtbar });
      // Den neuen Stand schickt der Server von sich aus an alle; hier ist
      // nichts nachzuhalten.
    } catch {
      this.patch({
        hint: 'Die Sichtbarkeit ließ sich nicht ändern. Besteht Verbindung?',
        hintSelfClearing: hinweisSchliesstSelbst('sichtbarkeit'),
      });
    }
  }

  /**
   * Die Ausfahrt beenden. Nur die Führung darf das.
   *
   * Damit verfällt der Beitrittscode, und die Löschfrist für alle
   * Positionsdaten beginnt zu laufen. Wer noch verbunden ist, wird vom Server
   * benachrichtigt und getrennt — auch dieses Gerät.
   *
   * Über die HTTP-Schnittstelle, nicht über das Protokoll: Ein Ende ist keine
   * Bewegungsmeldung, und die Antwort wird hier gebraucht.
   */
  async finishTour(): Promise<boolean> {
    const sitzung = this.#session;
    if (sitzung === null) {
      return false;
    }

    try {
      // Erst nachreichen: Nach dem Ende der Ausfahrt nimmt der Server nichts
      // mehr an, und die letzten Meldungen fehlten in der gemeinsamen Spur.
      await this.flushBuffer();
      await api.setTourState(sitzung.tourId, sitzung.sessionToken, 'finished');
      this.stop();
      return true;
    } catch {
      this.patch({
        hint: 'Die Ausfahrt ließ sich nicht beenden. Besteht Verbindung?',
        hintSelfClearing: hinweisSchliesstSelbst('ausfahrt_beenden'),
      });
      return false;
    }
  }

  /** „Gesehen" — die Warnung verschwindet vom Bildschirm, das Ereignis bleibt bestehen. */
  dismissWarning(participantId: string): void {
    this.patch({
      warnings: this.#state.warnings.filter((w) => w.participantId !== participantId),
    });
  }

  /**
   * Eine eingegangene Meldung wegtippen.
   *
   * Bei Meldungen der Führung geht die Quittung an den Server und von dort an
   * die Führung: Wer „Wir halten an" schickt, muss sehen, wer es gelesen hat.
   * Bei allen anderen bleibt das Wegtippen örtlich — niemand braucht die
   * Bestätigung über ein „muss tanken".
   */
  dismissStatus(id: string): void {
    const meldung = this.#state.incomingStatus.find((m) => m.id === id);
    this.#weggetippt.add(id);

    if (meldung !== undefined && isAcknowledged(meldung.kind)) {
      const hinaus = this.#connection?.send({ type: 'acknowledge_status', messageId: id }) ?? false;
      if (!hinaus) {
        this.#offeneQuittungen.add(id);
      }
    }

    this.patch({ incomingStatus: this.#state.incomingStatus.filter((m) => m.id !== id) });
  }

  clearNavigationRequest(): void {
    this.patch({ navigationRequestedAt: null });
  }

  clearNavigationEnd(): void {
    this.patch({ navigationEnded: null });
  }

  clearHint(): void {
    this.patch({ hint: null, hintSelfClearing: false });
  }

  /**
   * Die Ausfahrt verlassen.
   *
   * `successorId` gehört dazu, wenn das Führungsfahrzeug geht: Die Führung wird
   * übergeben, nicht niedergelegt. Diese Oberfläche fragt vorher danach und
   * schickt sie immer mit; die Prüfung des Servers ist der Rückhalt für andere
   * Clients — und der Grund, warum sie dort und nicht nur hier steht.
   */
  async leave(successorId?: string): Promise<void> {
    // Was noch wartet, gehört zu dieser Ausfahrt. Nach dem Abmelden nimmt der
    // Server es nicht mehr an — danach ist es endgültig verloren.
    await this.flushBuffer();

    this.#connection?.send(
      successorId === undefined ? { type: 'leave' } : { type: 'leave', successorId },
    );
    this.stop();
  }

  get session(): StoredSession | null {
    return this.#session;
  }
}

/** Eine Fahrt je Anwendung. */
export const drive = new DriveSession();
